import { useState } from 'react';
import { Expenditure, EditableExpenditure } from '../hooks/useExpenditures';
import { PanelBase } from './PanelBase';
import { TableViewBase } from './TableViewBase';
import { Frequency } from '../utils';
import { formatMoney } from 'src/common/currency';
import { showSuccess, showError } from 'src/toasts/toasts';
import { ExpenditureModal } from './ExpenditureModal';
import { useDisclosure } from '@mantine/hooks';
import { UnderlineTooltip } from 'src/UI/common';
import { useCurrency } from 'src/common/hooks/useCurrency';

type ExpenditurePanelProps = {
  items: Expenditure[];
  onSave: (data: EditableExpenditure[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
  frequency: Frequency;
};

export function ExpenditurePanel({
  items = [],
  onSave,
  onDelete,
  frequency,
}: ExpenditurePanelProps) {
  const currency = useCurrency();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedExpenditure, setSelectedExpenditure] = useState<Expenditure | undefined>();

  const handleSave = async (expenditure: Omit<EditableExpenditure, 'id' | 'clientId'>) => {
    try {
      await onSave([{ ...expenditure, id: selectedExpenditure?.id } as EditableExpenditure]);
      showSuccess('Expenditure saved successfully');
      closeModal();
      setSelectedExpenditure(undefined);
    } catch {
      showError('Failed to save expenditure');
    }
  };

  const handleEdit = (expenditure: Expenditure) => {
    setSelectedExpenditure(expenditure);
    openModal();
  };

  const handleAdd = () => {
    setSelectedExpenditure(undefined);
    openModal();
  };

  const handleDelete = (expenditure: Expenditure) => {
    if (expenditure.id) {
      onDelete([expenditure.id]);
    }
  };

  const renderTableView = () => (
    <TableViewBase
      items={items}
      columns={[
        {
          key: 'description',
          header: 'Description',
          sortable: true,
          renderValue: value => {
            const str = String(value ?? '');
            return str.length > 50 ? `${str.slice(0, 50)}...` : str;
          },
        },
        { key: 'category', header: 'Category', sortable: true },
        { key: 'frequency', header: 'Frequency', sortable: true },
        {
          key: frequency === 'monthly' ? 'amountMonthly' : 'amountAnnually',
          header: `Spend (${frequency})`,
          sortable: true,
          sortKey: frequency === 'monthly' ? 'amountMonthly' : 'amountAnnually',
          renderValue: (_value, item) => {
            const displayValue = frequency === 'monthly' ? item.amountMonthly : item.amountAnnually;
            const tooltipValue = frequency === 'monthly' ? item.amountAnnually : item.amountMonthly;
            return (
              <UnderlineTooltip
                label={`${
                  frequency === 'monthly' ? 'Annually' : 'Monthly'
                }: ${formatMoney(tooltipValue, currency)}`}
              >
                {formatMoney(displayValue, currency)}
              </UnderlineTooltip>
            );
          },
          align: 'right',
        },
      ]}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <PanelBase<Expenditure>
        items={items}
        addButtonLabel="Add a new expenditure"
        renderTableView={renderTableView}
        onAdd={handleAdd}
      />
      <ExpenditureModal
        opened={modalOpened}
        onClose={closeModal}
        onSubmit={handleSave}
        initialValues={selectedExpenditure}
      />
    </>
  );
}
